import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { PageLoader, SkeletonLoader } from '../components/ui/loader';
import { 
  FileText, 
  AlertTriangle, 
  CheckCircle2, 
  Upload, 
  TrendingUp,
  Languages,
  FileX,
  FileWarning,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
  const { api } = useAuth();
  const [summary, setSummary] = useState(null);
  const [recentIssues, setRecentIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, issuesRes] = await Promise.all([
        api().get('/reports/summary'),
        api().get('/issues?limit=5')
      ]);
      setSummary(summaryRes.data);
      setRecentIssues(issuesRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const stats = summary ? [
    { 
      label: 'Total Documents', 
      value: summary.total_documents, 
      icon: FileText, 
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    { 
      label: 'Validated', 
      value: summary.validated_documents, 
      icon: CheckCircle2, 
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    { 
      label: 'With Issues', 
      value: summary.documents_with_issues, 
      icon: AlertTriangle, 
      color: 'text-amber-600',
      bgColor: 'bg-amber-50'
    },
    { 
      label: 'Avg Match', 
      value: `${summary.average_match_percentage}%`, 
      icon: TrendingUp, 
      color: 'text-violet-600',
      bgColor: 'bg-violet-50'
    },
  ] : [];

  const issueStats = summary ? [
    { label: 'Wrong Language', value: summary.wrong_language_count, icon: Languages, color: 'text-amber-600' },
    { label: 'Missing Content', value: summary.missing_content_count, icon: FileX, color: 'text-red-600' },
    { label: 'Improper Format', value: summary.improper_format_count, icon: FileWarning, color: 'text-indigo-600' },
    { label: 'Wrong Content', value: summary.wrong_content_count, icon: FileText, color: 'text-pink-600' },
  ] : [];

  const getIssueTypeClass = (type) => {
    const classes = {
      wrong_language: 'issue-wrong_language',
      missing_content: 'issue-missing_content',
      improper_format: 'issue-improper_format',
      wrong_content: 'issue-wrong_content'
    };
    return classes[type] || 'bg-gray-100 text-gray-800';
  };

  const getSeverityClass = (severity) => {
    const classes = {
      high: 'severity-high',
      medium: 'severity-medium',
      low: 'severity-low'
    };
    return classes[severity] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <PageLoader text="Loading dashboard..." />;
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Overview of your document verification status</p>
        </div>
        <Link to="/upload">
          <Button className="gap-2" data-testid="upload-document-btn">
            <Upload className="w-4 h-4" />
            Upload Document
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Card key={stat.label} className={`stagger-${index + 1} animate-fade-in`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{stat.label}</p>
                  <p className="text-3xl font-bold tracking-tight mt-2 font-['Manrope']" data-mono="true">
                    {stat.value}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Issue Breakdown & Recent Issues */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Issue Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">Issue Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {issueStats.map((item) => (
                <div key={item.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <item.icon className={`w-5 h-5 ${item.color}`} />
                    <span className="text-sm">{item.label}</span>
                  </div>
                  <span className="font-mono text-lg font-semibold">{item.value}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t">
              <Link to="/issues">
                <Button variant="ghost" className="w-full justify-between" data-testid="view-all-issues-btn">
                  View all issues
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Recent Issues */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">Recent Issues</CardTitle>
          </CardHeader>
          <CardContent>
            {recentIssues.length > 0 ? (
              <div className="space-y-4">
                {recentIssues.map((issue) => (
                  <div key={issue.id} className="flex items-start justify-between gap-4 pb-4 border-b last:border-0 last:pb-0">
                    <div className="flex-1 min-w-0">
                      <Link 
                        to={`/documents/${issue.document_id}`}
                        className="font-medium text-sm hover:text-primary truncate block"
                      >
                        {issue.document_name}
                      </Link>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{issue.description}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge variant="outline" className={`text-xs ${getIssueTypeClass(issue.issue_type)}`}>
                        {issue.issue_type.replace('_', ' ')}
                      </Badge>
                      <Badge variant="outline" className={`text-xs ${getSeverityClass(issue.severity)}`}>
                        {issue.severity}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                <p>No issues found</p>
                <p className="text-sm">All documents are validated successfully</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold font-['Manrope']">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link to="/upload" className="block">
              <div className="p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group">
                <Upload className="w-6 h-6 mb-2 group-hover:text-primary transition-colors" />
                <h3 className="font-medium">Upload Document</h3>
                <p className="text-sm text-muted-foreground">Add new PDF or DOCX files</p>
              </div>
            </Link>
            <Link to="/validation-rules" className="block">
              <div className="p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group">
                <CheckCircle2 className="w-6 h-6 mb-2 group-hover:text-primary transition-colors" />
                <h3 className="font-medium">Configure Rules</h3>
                <p className="text-sm text-muted-foreground">Set validation criteria</p>
              </div>
            </Link>
            <Link to="/reports" className="block">
              <div className="p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group">
                <TrendingUp className="w-6 h-6 mb-2 group-hover:text-primary transition-colors" />
                <h3 className="font-medium">View Reports</h3>
                <p className="text-sm text-muted-foreground">Export validation data</p>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
